import { Head, router } from "@inertiajs/react"
import { Pencil, Trash2 } from "lucide-react"

import { columns } from "@/components/agents/columns"
import { DataTable } from "@/components/ui/data-table"
import AdminLayout from "@/layouts/admin-layout"
import { adminAgentPath, adminAgentsPath, editAdminAgentPath } from "@/routes"
import type { Agent, BreadcrumbItem, Pagy } from "@/types"

type InstanceKind = "standard" | "custom" | null

interface AgentsProps {
  agents: Agent[]
  pagy: Pagy
  sort: string | null
  q: {
    name_cont?: string
    state_in?: string[]
  }
  kind?: InstanceKind
}

function buildBreadcrumbs(kind: InstanceKind): BreadcrumbItem[] {
  const crumbs: BreadcrumbItem[] = [
    { title: "Instances", href: adminAgentsPath() },
  ]
  if (kind === "standard") {
    crumbs.push({
      title: "Standard",
      href: `${adminAgentsPath()}?kind=standard`,
    })
  } else if (kind === "custom") {
    crumbs.push({
      title: "Custom",
      href: `${adminAgentsPath()}?kind=custom`,
    })
  }
  return crumbs
}

export default function Agents({ agents, pagy, kind, sort }: AgentsProps) {
  const breadcrumbs = buildBreadcrumbs(kind ?? null)
  const title = kind
    ? `${kind[0].toUpperCase()}${kind.slice(1)} agents`
    : "Instances"

  function handleDelete(agent: Agent) {
    router.delete(adminAgentPath({ id: agent.uid }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title={title} />
      <DataTable
        columns={columns}
        data={agents}
        pagy={pagy}
        sort={sort}
        onSortChange={(newSort) => {
          router.visit(adminAgentsPath(), {
            data: { kind: kind ?? undefined, sort: newSort },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        onPageChange={(page) => {
          router.visit(adminAgentsPath(), {
            data: {
              page: page,
              kind: kind ?? undefined,
              sort: sort ?? undefined,
            },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        emptyComponent={
          <div className="text-muted-foreground py-10 text-center">
            No agents found.
          </div>
        }
        actions={[
          {
            label: "Edit",
            icon: <Pencil className="h-4 w-4" />,
            onClick: (agent: Agent) => {
              router.visit(editAdminAgentPath({ id: agent.uid }))
            },
            variant: "default",
          },
          {
            label: "Delete",
            icon: <Trash2 className="h-4 w-4" />,
            onClick: handleDelete,
            variant: "destructive",
          },
        ]}
      />
    </AdminLayout>
  )
}
